/**
 * Forgot Password Page - Trade Loop v2.0
 *
 * Migrated from v1.0 with cookie-based authentication
 * Allows users to request a password reset email
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { resetPassword } from "@/services/authService";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsSubmitted(true);
      showSuccess(
        "Email sent!",
        "Check your inbox for password reset instructions"
      );
    } catch (error: any) {
      showError(
        "Failed to send reset email",
        error.message || "Please try again later"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
        <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
          <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          </div>
        </header>

        <main className="px-4 py-8">
          <div className="max-w-sm mx-auto">
            <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500">
              <CardContent className="px-6 py-10 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300 delay-150">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                  Check Your Email
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  We've sent password reset instructions to{" "}
                  <span className="font-semibold text-gray-900">{email}</span>
                </p>
                <Link to="/login">
                  <Button className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]">
                    Back to Sign In
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <div className="text-center mt-8">
              <p className="text-sm text-gray-500">
                Didn't receive the email?{" "}
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                >
                  Try again
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
      {/* Header */}
      <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
        <div className="flex items-center justify-between max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <Link to="/login">
            <Button variant="ghost" size="sm" className="p-3 hover:bg-white/60 rounded-xl backdrop-blur-sm border border-white/20 transition-all duration-200">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </Button>
          </Link>
          <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          <div className="w-11"></div>
        </div>
      </header>

      {/* Forgot Password Form */}
      <main className="px-4 py-8">
        <div className="max-w-sm mx-auto">
          <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0 rounded-2xl animate-in slide-in-from-bottom-4 duration-500">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
                Reset Password
              </CardTitle>
              <p className="text-gray-600 text-lg leading-relaxed">
                Enter your email and we'll send you reset instructions
              </p>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="h-12 bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={!email || isLoading}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:shadow-none"
                >
                  {isLoading ? "Sending..." : "Send Reset Instructions"}
                </Button>

                <div className="text-center pt-2">
                  <Link to="/login">
                    <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
